/**
 * Load domain schemas — canonical shape for TMS load records.
 * Enum constants are exported as values (for runtime use) AND used in schemas.
 */
import { z } from 'zod';
import { TenantIdSchema, UlidSchema, IsoDateSchema, MoneySchema, EmailSchema, PhoneSchema } from './_primitives.js';
import { ConsolidationStatusEnum } from './bundle.js';

// ── Enums ─────────────────────────────────────────────────────────────────────

export const LOAD_ZONE = Object.freeze({ Z5: 5, Z6: 6, Z7: 7 });
export const LoadZoneEnum = z.enum([5, 6, 7].map(String)).transform(Number).or(z.number().int().min(5).max(7));

export const LOAD_STATUS = Object.freeze({
  CREATED:     'created',
  TENDERED:    'tendered',
  CONFIRMED:   'confirmed',
  PICKING_UP:  'picking_up',
  IN_TRANSIT:  'in_transit',
  DELIVERED:   'delivered',
  CLOSED:      'closed',
  CANCELLED:   'cancelled',
});

export const LOAD_MODE = Object.freeze({
  FTL:       'ftl',
  LTL:       'ltl',
  INTERMODAL:'intermodal',
  PARCEL:    'parcel',
  DRAYAGE:   'drayage',
  RAIL:      'rail',
  AIR:       'air',
  OCEAN:     'ocean',
});

export const LoadStatusEnum = z.enum(Object.values(LOAD_STATUS));
export const LoadModeEnum   = z.enum(Object.values(LOAD_MODE));

// Load exception lifecycle status — TMS-V2. Consumed by
// src/modules/tms/services/LoadDataService.js (EXCEPTION_STATUS.OPEN/.RESOLVED).
// Restored here because LoadDataService imported EXCEPTION_STATUS from
// @kairos/types but the definition was never added — same omission as
// CARRIER_PAYMENT_STATUS (TMS-TYPES-CARRIER-PAYMENT-STATUS-HOTFIX-1). Values are
// the snake_case forms already used as record values across the codebase.
export const EXCEPTION_STATUS = Object.freeze({
  OPEN:     'exception_open',
  RESOLVED: 'exception_resolved',
});

export const ExceptionStatusEnum = z.enum(Object.values(EXCEPTION_STATUS));

// ── Stop schema ───────────────────────────────────────────────────────────────

const StopSchema = z.object({
  type:           z.enum(['pickup', 'delivery', 'relay']),
  address:        z.string().min(1),
  city:           z.string().optional(),
  state:          z.string().optional(),
  zip:            z.string().optional(),
  country:        z.string().optional(),
  scheduledAt:    IsoDateSchema.optional(),
  arrivedAt:      IsoDateSchema.optional(),
  completedAt:    IsoDateSchema.optional(),
  contactName:    z.string().optional(),
  contactPhone:   PhoneSchema.optional(),
  contactEmail:   EmailSchema.optional(),
  notes:          z.string().optional(),
  sequence:       z.number().int().nonnegative().optional(),
});

// ── Financials sub-schema ─────────────────────────────────────────────────────

const LoadFinancialsSchema = z.object({
  revenue:       MoneySchema.optional(),
  cost:          MoneySchema.optional(),
  margin:        MoneySchema.optional(),
  marginPercent: z.number().optional(),
  fuelSurcharge: MoneySchema.optional(),
  accessorials:  MoneySchema.optional(),
}).optional();

// ── Exception sub-schema ──────────────────────────────────────────────────────

const LoadExceptionSchema = z.object({
  id:          z.string().min(1),
  type:        z.string().min(1),
  description: z.string().optional(),
  createdAt:   IsoDateSchema.optional(),
  resolvedAt:  IsoDateSchema.optional(),
  resolvedBy:  z.string().optional(),
});

// ── Root Load schema ──────────────────────────────────────────────────────────

export const LoadSchema = z.object({
  // Identity (required)
  tenantId:    TenantIdSchema,

  // Optional system-assigned ID (present after Firestore write)
  id:          z.string().optional(),

  // Core fields
  loadNumber:  z.string().optional(),
  status:      LoadStatusEnum,
  mode:        LoadModeEnum,

  // Parties
  customerId:  z.string().optional(),
  carrierId:   z.string().optional(),
  carrierName: z.string().optional(),

  // Stops
  origin:      StopSchema.optional(),
  destination: StopSchema.optional(),
  stops:       z.array(StopSchema).optional(),

  // Cargo
  commodity:   z.string().optional(),
  weight:      z.number().nonnegative().optional(),
  pieces:      z.number().int().nonnegative().optional(),
  pallets:     z.number().int().nonnegative().optional(),

  // Financials
  financials:  LoadFinancialsSchema,

  // AEGIS
  aegisRiskScore:    z.number().min(0).max(100).optional(),
  aegisConfidence:   z.number().min(0).max(1).optional(),
  exceptions:        z.array(LoadExceptionSchema).optional(),

  // Owned fleet (optional)
  ownedFleet: z.object({
    vehicleId:  z.string().optional(),
    driverId:   z.string().optional(),
  }).optional(),

  // Driver
  driver: z.object({
    driverId:   z.string().optional(),
    driverName: z.string().optional(),
    phone:      PhoneSchema.optional(),
  }).optional(),

  // TMS-CT-2 SEAT-E: multi-zone load board fields (schema v2.1)
  zone:         z.number().int().min(5).max(7).optional(),
  sequenceRank: z.number().int().nonnegative().optional(),
  shipperId:    z.string().optional(),
  originZip:    z.string().optional(),
  destZip:      z.string().optional(),
  baseRate:     z.number().nonnegative().optional(),
  surgeFactor:  z.number().nonnegative().default(1.0).optional(),
  softDeleted:  z.boolean().optional(),

  // Consolidation (TMS-CONSOLIDATION-SURFACE-1)
  consolidationStatus: ConsolidationStatusEnum.nullable().optional(),
  bundleId:            z.string().nullable().optional(),

  // Carrier assignment (TMS-CARRIER-VETTING-SURFACE-1)
  assignedCarrierId:        z.string().optional(),
  carrierAssignmentStatus:  z.enum(['UNASSIGNED', 'ASSIGNED', 'CONFIRMED', 'IN_TRANSIT']).optional(),
  assignedAt:               z.union([IsoDateSchema, z.object({})]).optional(),

  // Metadata
  isDeleted:   z.boolean().optional(),
  createdAt:   z.union([IsoDateSchema, z.object({})]).optional(),
  updatedAt:   z.union([IsoDateSchema, z.object({})]).optional(),
  createdBy:   z.string().optional(),
  updatedBy:   z.string().optional(),
  version:     z.number().int().optional(),

  // Catch-all for extension fields without breaking existing callers
}).passthrough();

/** @typedef {import('zod').infer<typeof LoadSchema>} Load */

// Stop micro-state — TMS-V2 load stop lifecycle. Values match the stop
// micro_state strings already written by LoadDataService.updateStop and the
// EDI-214/204 handlers ('pending' | 'arrived' | 'completed'). Consumed by
// src/modules/tms/screens/ShipmentDetail.jsx. Restored — imported by B2 but
// never defined (TMS-TYPES-WORKSPACE-STOPSTATE-HOTFIX-1).
export const STOP_MICRO_STATE = Object.freeze({
  PENDING:   'pending',
  ARRIVED:   'arrived',
  COMPLETED: 'completed',
});

export const StopMicroStateEnum = z.enum(Object.values(STOP_MICRO_STATE));
